import React, { useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/Layout';
import { GoBackButtonFilled } from '@/components/buttons/Buttons';
import { cn } from '@/components/ui/cn';
import { loadGoogleMaps as waitGoogleMapsLoad } from '../../../../common/loadGoogleMaps';
import { placesRepository, X4bLocation } from '../../views/PlacesRepository';
import { useForm } from 'react-hook-form';
import PickAddressInput from './PickAddressInput';
import { showTypedModal } from '@/common/common_utils';
import ConfirmAddressModal from './ConfirmAddressModal';

interface PickAddressMapModalProps {
	isOpen: boolean;
	handleClose: () => void;
	initialLatitude?: number;
	initialLongitude?: number;
	onLocationSelected: (location: X4bLocation) => void;
}

const PickAddressMapModal: React.FC<PickAddressMapModalProps> = (props) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const [error, setError] = useState<string | null>(null);
	const markerRef = useRef<google.maps.Marker | null>(null);
	const googleMapRef = useRef<google.maps.Map | null>(null);
	const { control } = useForm<{ address: X4bLocation }>();

	// Handle location picked by user
	const handleLocationPicked = async (position: google.maps.LatLng) => {
		if (!props.onLocationSelected) return;

		try {
			const location = await placesRepository.reverseGeocode({
				latitude: position.lat(),
				longitude: position.lng()
			});

			if (location) {
				const confirmed = await showTypedModal(ConfirmAddressModal, { location: location });
				if (confirmed) {
					props.onLocationSelected(location);
				}
			}
		} catch (error) {
			console.error('Failed to reverse geocode location:', error);
		}
	};

	// Handle location selected from search input
	const handleSearchLocationSelected = async (location: X4bLocation) => {
		if (!location.Latitude || !location.Longitude) return;

		const position = new google.maps.LatLng(location.Latitude, location.Longitude);

		// Update marker position
		if (markerRef.current) {
			markerRef.current.setPosition(position);
		}

		// Center map on new location
		if (googleMapRef.current) {
			googleMapRef.current.setCenter(position);
			//googleMapRef.current.setZoom(15);
		}

		const confirmed = await showTypedModal(ConfirmAddressModal, { location: location });
		if (confirmed) {
			// Notify parent component
			props.onLocationSelected(location);
		}
	};

	// Initialize map
	useEffect(() => {
		if (!props.isOpen) return;

		const initMap = async () => {
			setError(null);
			try {
				// Load Google Maps script
				await waitGoogleMapsLoad();

				// Try to load saved map position from localStorage
				const savedMapState = localStorage.getItem('conciergeMapState');
				let initialCenter: google.maps.LatLng;
				let initialZoom: number = 15;

				// Priority: 1. Props coordinates, 2. Saved state, 3. Default
				if (props.initialLatitude !== undefined && props.initialLongitude !== undefined) {
					// Use provided initial coordinates
					initialCenter = new google.maps.LatLng(props.initialLatitude, props.initialLongitude);
				} else if (savedMapState) {
					try {
						const { center, zoom } = JSON.parse(savedMapState);
						initialCenter = new google.maps.LatLng(center.lat, center.lng);
						initialZoom = zoom;
					} catch (e) {
						console.warn('Failed to parse saved map state, using defaults', e);
						initialCenter = new google.maps.LatLng(14.6350728, 120.9927488);
					}
				} else {
					// Set default center if no saved state
					initialCenter = new google.maps.LatLng(14.6350728, 120.9927488);
				}

				// Create a simple Google Map
				const googleMap = new google.maps.Map(mapRef.current!, {
					center: initialCenter,
					zoom: initialZoom,
					mapTypeControl: false,
					streetViewControl: false,
					fullscreenControl: false,
				});

				googleMapRef.current = googleMap;

				// Create draggable marker at initial position
				const marker = new google.maps.Marker({
					position: initialCenter,
					map: googleMap,
					draggable: true,
					title: 'Drag to select location',
					icon: {
						url: '/images/concierge/pick-address-icon.svg',
						scaledSize: new google.maps.Size(104, 104),
						anchor: new google.maps.Point(52, 52)
					}
				});

				markerRef.current = marker;

				// Handle marker drag end
				marker.addListener('dragend', () => {
					const position = marker.getPosition();
					if (position) {
						handleLocationPicked(position);
					}
				});

				// Handle map clicks to move marker
				googleMap.addListener('click', (event: google.maps.MapMouseEvent) => {
					if (event.latLng) {
						marker.setPosition(event.latLng);
						handleLocationPicked(event.latLng);
					}
				});

			} catch (error) {
				console.error('Failed to initialize map:', error);
				setError(error instanceof Error ? error.message : 'Failed to initialize map');
			}
		};

		if (mapRef.current) {
			initMap();
		}

		return () => {
			// Cleanup marker
			if (markerRef.current) {
				markerRef.current.setMap(null);
				markerRef.current = null;
			}
			// Cleanup map reference
			googleMapRef.current = null;
		};
	}, [props.isOpen, props.initialLatitude, props.initialLongitude]);

	if (!props.isOpen) return null;

	return (
		<div className="fixed inset-0 bg-surface justify-center items-center z-50">
			<Layout includeSidebar={false}>
				<div className="flex flex-col" style={{ height: 'calc(100vh - 132px)' }}>
					{error ? (
						<div className={cn('w-full flex-1 !min-h-0 rounded-[28px] flex items-center justify-center bg-gray-100')}>
							<div style={{ color: '#dc3545' }}>Error: {error}</div>
						</div>
					) : (
						<div
							ref={mapRef}
							style={{ width: '100%', height: '100%' }}
							className={cn('w-full flex-1 !min-h-0 object-cover rounded-[28px]')}
							id="pick-address-map"
						/>
					)}
					<div className={'absolute top-[48px] left-[48px] flex flex-col gap-4'}>
						<PickAddressInput
							className="w-[500px]"
							onLocationSelected={handleSearchLocationSelected}
						/>
						<GoBackButtonFilled
							id="map-back"
							onClick={props.handleClose}
						/>
					</div>
				</div>
			</Layout>
		</div>
	);
};

export default PickAddressMapModal;
